import type { Component } from 'vue'
import type { FieldLabelMark } from '@ds'

import {
  AssetWizardAccount,
  AssetWizardAnswer,
  AssetWizardCashFlow,
  AssetWizardEntity,
  AssetWizardField,
  AssetWizardInvestor,
  AssetWizardValuation,
} from '@/store/ai/assetWizard/utils/types'


import type { TagRef } from '@/entities/tags/utils/types'
import { AssetClass } from '@/entities/assets'
import { AssetPriceSource } from '@/entities/assets/components/utils/enums'
import { ASSET_PRICE_SOURCE_EDITABLE } from '@/entities/assets/components/utils/const'
import { commonTags } from '@/entities/tags/utils/helpers'
import { stringToLocalDateString } from '@/helpers/dates'

import {
  ACCOUNT_MODE_FIELD,
  ACCOUNT_NAME_FIELD,
  ASSET_NAME_FIELD,
  CASH_FLOWS_FIELD,
  CASH_FLOWS_STEP_KEY,
  CONTRIBUTION_TYPES,
  DISTRIBUTION_TYPES,
  ENTITIES_STEP_KEY,
  INVESTORS_STEP_KEY,
  MARKS_FIELD,
  MORTGAGE_ACCOUNT_MODE_FIELD,
  OWNER_ACCOUNT_ID_FIELD,
  SUMMARY_STEP_KEY,
  TAGS_STEP_KEY,
  VALUE_STEP_KEY,
} from './const'
import { isInvestorResolved } from './schema'
import {
  WizardAccountMode,
  WizardCashFlow,
  WizardDefaultSource,
  WizardDefinition,
  WizardFieldDef,
  WizardFieldOrigin,
  WizardInvestor,
  WizardMark,
  WizardMissingField,
  WizardStep,
  WizardStepDef,
  WizardSeedContext,
  WizardStepState,
  WizardValues,
} from './types'

/** Every field the wizard collects, in the order its steps ask for them. */
export const allFields = (definition: WizardDefinition): WizardFieldDef[] =>
  definition.steps.flatMap(step => step.fields ?? [])

export const fieldByName = (definition: WizardDefinition, name: string) =>
  allFields(definition).find(field => field.name === name)

/**
 * Fields the user actually sees. A value the flow fixes — a loan's type is
 * `loan` — is not a question, and showing it would be asking them to confirm
 * what they cannot change.
 */
export const visibleFields = (
  fields: WizardFieldDef[],
  origins: Record<string, WizardFieldOrigin>,
): WizardFieldDef[] =>
  fields.filter(field => origins[field.name] !== WizardFieldOrigin.FIXED)

/**
 * Whether this field is owed, for these values. A boolean stays a boolean; a
 * predicate is asked. The rail's counts, the footer and the origin marks all go
 * through here, so a conditionally required field cannot be counted one way in
 * one place and another way in the next.
 */
export const isFieldRequired = (field: WizardFieldDef, values: WizardValues) =>
  typeof field.required === 'function' ? field.required(values) : !!field.required

/**
 * What the asset opens tagged with: the flow's own tags, whatever they are —
 * nothing when it has none.
 *
 * Unconditional, on both the assisted and the manual path: the initial read
 * never guesses tags at all (that question belongs to the separate
 * ai_tag_suggestion assistant, asked only when the user clicks "Ask AI" on the
 * tags step), so there is nothing here for a read to override in the first
 * place. A flow's default is a starting point for a wizard opened cold, and
 * it stays one whichever way the wizard was opened.
 *
 * Copied as pairs with no id: whether the repository has them is a fact about
 * the catalogue, resolved later by `withTagId`, and writing an id onto the
 * definition's list would leak one wizard's catalogue into the next. A pair
 * the repository does not have is still a chip — TagRef does not need an id
 * to display, and create POSTs whatever the catalogue lacks.
 *
 * Category and Subcategory are dropped. A wizard does not file anything under
 * the app's category tree — see `isCommonTag` — so a default naming one is
 * not applied, whichever wizard defined it.
 */
const seedTags = (defaults: TagRef[] = []): TagRef[] =>
  commonTags(defaults).map(tag => ({ tag_name: tag.tag_name, tag_value: tag.tag_value }))

/**
 * The recorded values, newest first — the order a reader wants, which is not
 * the order they were entered in. A copy: the stored list stays a plain record
 * of what was added.
 */
export const sortedMarks = (marks: WizardMark[] = []): WizardMark[] =>
  [...marks].sort((a, b) => b.date.localeCompare(a.date))

/**
 * The value the asset actually carries: the latest override if there is one,
 * otherwise the latest mark.
 *
 * An override replaces the carrying value from its date forward and every later
 * manual mark is ignored until the next override — so calling the newest row
 * the carrying value whatever its source would have the wizard state something
 * the app does not do. Shared by the step and the summary rather than written
 * twice, because two copies of this rule would disagree on the day one changed.
 */
export const carryingMark = (marks: WizardMark[] = []): WizardMark | undefined => {
  const sorted = sortedMarks(marks)
  return sorted.find(mark => mark.source === AssetPriceSource.OVERRIDE) ?? sorted[0]
}

/**
 * Where a recorded value came from, in one line: the kind of mark it is, and
 * whether the user entered it or the wizard carried their purchase price into
 * it.
 */
export const markCaption = (mark: WizardMark): string => {
  const kind =
    ASSET_PRICE_SOURCE_EDITABLE.find(option => option.value === mark.source)?.label ?? mark.source

  return mark.fromPurchase ? `Purchase price · ${kind}` : `${kind} · entered by you`
}

/**
 * The step that asks which account the records hang off, for a flow that asks
 * at all.
 *
 * Found by the field it collects rather than by its key: a flow with a held-in
 * account of its own names that step `ownership` too — it is the same question
 * to the user — so the key says where the step is, not what it collects.
 */
export const ownershipStepOf = (definition: WizardDefinition) =>
  definition.steps.find(step =>
    step.fields?.some(field => field.name === OWNER_ACCOUNT_ID_FIELD),
  )

/**
 * Whether this flow collects an account a holding is *held in* — and so whether
 * `accountId` means anything to it.
 *
 * The three flows that keep their own account step (fine art, a fund, and the
 * financed property's lender) are exactly the three whose create resolves that
 * name against an existing account, and exactly the three whose schema declares
 * the id. A flow that hangs its record off the ownership step instead has no
 * held-in account at all, and carrying an empty id in its values was state no
 * schema described and no create read.
 */
const holdsAccount = (definition: WizardDefinition) =>
  allFields(definition).some(field => field.name === ACCOUNT_NAME_FIELD)

/**
 * The step that records dated values, for a flow that records them — the
 * valuation step as a component rather than as the placeholder the other flows
 * still carry.
 *
 * Same shape as `ownershipStepOf` and for the same reason: a list in the values
 * of a wizard that neither collects nor writes it is state nothing describes,
 * and a summary block for a step that is not there would state an empty one.
 */
export const valueStepOf = (definition: WizardDefinition) =>
  definition.steps.find(step => step.key === VALUE_STEP_KEY && !!step.component)

const recordsMarks = (definition: WizardDefinition) => !!valueStepOf(definition)

/**
 * The step that splits a commitment across more than one investing entity —
 * private_fund_subscribe_and_commit's own, and private_security's own,
 * checked by key rather than by field the way `ownershipStepOf` is:
 * `investors` is a bespoke component's own state, never a WizardFieldDef, so
 * there is no field name to look for.
 */
export const investorsStepOf = (definition: WizardDefinition) =>
  definition.steps.find(step => step.key === INVESTORS_STEP_KEY)

const hasInvestors = (definition: WizardDefinition) => !!investorsStepOf(definition)

/**
 * The step that lists more than one entity each independently holding the
 * same fund position — private_fund's own. Same shape as `investorsStepOf`
 * and checked the same way, but never the same step: `withEntities` asks no
 * share of anything, so a row here is done once its account is, never once
 * a split adds up.
 */
export const entitiesStepOf = (definition: WizardDefinition) =>
  definition.steps.find(step => step.key === ENTITIES_STEP_KEY)

export const hasEntities = (definition: WizardDefinition) => !!entitiesStepOf(definition)

/**
 * Whether this flow collects more than one investing entity at all, split or
 * not — `hasInvestors`/`hasEntities` are mutually exclusive per flow, so this
 * is true for exactly one of the two. Used where the two steps' rows share
 * one slot in `values.investors` and the caller does not need to know which
 * kind of split, if any, the flow itself asks for — `wizardDefaults`'s own
 * starting row.
 */
const hasAnyInvestingStep = (definition: WizardDefinition) =>
  hasInvestors(definition) || hasEntities(definition)

/**
 * The step that logs capital calls, fees and distributions against a fund's
 * own investors — private_fund's own, same shape as `investorsStepOf`: a
 * bespoke component's state, never a WizardFieldDef, so there is no field
 * name to look for.
 */
export const cashFlowsStepOf = (definition: WizardDefinition) =>
  definition.steps.find(step => step.key === CASH_FLOWS_STEP_KEY && !!step.component)

const hasCashFlows = (definition: WizardDefinition) => !!cashFlowsStepOf(definition)

/** Money moving into the fund — a capital call, a fee. */
export const isContribution = (type: string) => CONTRIBUTION_TYPES.includes(type)

/** Money moving out to the investor — a return, a dividend, income. */
export const isDistribution = (type: string) => DISTRIBUTION_TYPES.includes(type)

/**
 * Oldest first — the way a capital account statement actually reads. A copy:
 * the stored list stays in the order rows were entered, same rule
 * `sortedMarks` follows for the valuation step.
 */
export const sortedCashFlows = (cashFlows: WizardCashFlow[]): WizardCashFlow[] =>
  [...cashFlows].sort((a, b) => a.date.localeCompare(b.date))

/**
 * The cash-flows step's own three stat-card numbers, and the row list's own
 * order — all four read off the same list, kept together so a call site never
 * derives two of them from different filters that quietly drift.
 *
 * Every figure is a magnitude across every row, whether the step logs it
 * against one owner account or several investors — the step shows what has
 * been called and returned in total, not one row's own share of it.
 */
export const calledToDate = (cashFlows: WizardCashFlow[]): number =>
  cashFlows.filter(flow => isContribution(flow.type)).reduce((sum, flow) => sum + flow.amount, 0)

export const distributedToDate = (cashFlows: WizardCashFlow[]): number =>
  cashFlows.filter(flow => isDistribution(flow.type)).reduce((sum, flow) => sum + flow.amount, 0)

export const distributionCount = (cashFlows: WizardCashFlow[]): number =>
  cashFlows.filter(flow => isDistribution(flow.type)).length

/** Called less distributed — what has actually been drawn, net. */
export const netContributed = (cashFlows: WizardCashFlow[]): number =>
  calledToDate(cashFlows) - distributedToDate(cashFlows)

/** What is left of the commitment that has not been called yet. */
export const unfunded = (cashFlows: WizardCashFlow[], commitment?: number): number | undefined =>
  commitment === undefined ? undefined : commitment - calledToDate(cashFlows)

/** One investor row, empty. What the step and a cold open both start from. */
export const emptyInvestor = (): WizardInvestor => ({
  accountMode: WizardAccountMode.EXISTING,
  accountId: '',
  accountName: '',
  accountType: '',
  share: undefined,
})

/**
 * This investor's own slice of the commitment, as a percent — undefined
 * until both the share and the commitment it is a share of are known.
 * Shared between the investing-entities step's editable Percent field and
 * the allocation bar's read-only legend, so the two can never round it
 * differently.
 */
export const investorPercent = (investor: WizardInvestor, commitment?: number): number | undefined =>
  commitment && investor.share !== undefined
    ? Math.round((investor.share / commitment) * 1000) / 10
    : undefined

/*
 * Cycles rather than one per row: a split can have more investors than
 * colours to spare. `@ds` has no dedicated categorical/chart palette (see
 * ds-tokens.css) — these five are the ones its own token doc names as
 * fair game for "dots, bars, chart series and icon glyphs".
 */
const INVESTOR_BAR_COLORS = [
  'rgb(var(--ds-primary))',
  'rgb(var(--ds-info))',
  'rgb(var(--ds-success))',
  'rgb(var(--ds-warning))',
  'rgb(var(--ds-destructive))',
  'rgb(var(--ds-primary) / 0.5)',
  'rgb(var(--ds-info) / 0.5)',
  'rgb(var(--ds-success) / 0.5)',
  'rgb(var(--ds-warning) / 0.5)',
  'rgb(var(--ds-destructive) / 0.5)',
]

/** Which colour marks one investor's own row, dot and bar segment alike — the
 * investing-entities step and the allocation bar both key off the same index. */
export const investorBarColor = (index: number) => INVESTOR_BAR_COLORS[index % INVESTOR_BAR_COLORS.length]

/**
 * Empty everywhere. A wizard opened without the assistant starts from nothing —
 * except the ownership mode, which is a choice between two shapes rather than a
 * value, and has to start on one of them, and except a flow's own tags, which
 * are applied the way its type is fixed.
 */
export const wizardDefaults = (definition: WizardDefinition): WizardValues => {
  const values: WizardValues = {
    tags: seedTags(definition.defaultTags),
    [ACCOUNT_MODE_FIELD]: WizardAccountMode.EXISTING,
  }
  /*
   * Which existing account `accountName` names, when it names one. Like `tags`,
   * part of the state without being a field — nothing renders it, and only the
   * create reads it. Empty means "no account matched", which is what makes the
   * create make one. Only for a flow that has such an account — see
   * `holdsAccount`.
   *
   * `MORTGAGE_ACCOUNT_MODE_FIELD` needs the same starting point
   * `ACCOUNT_MODE_FIELD` gets above, for the same reason: a step that renders
   * on `mode === NEW` needs a real value to compare against from the first
   * render, not `undefined` — which matches neither branch and leaves the
   * step showing an existing-account picker nobody's account is selected in.
   * Only the one flow with this account today, real_estate_with_financing,
   * has this field to seed — `holdsAccount` is what that flow is recognised
   * by, the same way it recognises `accountId` above.
   */
  if (holdsAccount(definition)) {
    values.accountId = ''
    values[MORTGAGE_ACCOUNT_MODE_FIELD] = WizardAccountMode.EXISTING
  }
  /*
   * One empty row, not none: the step — whichever of the two a flow keeps,
   * splitting a commitment or listing entities that each hold their own
   * position — is what asks for it, and a step with nothing to show would
   * have to invent the first row itself rather than reading it off the
   * values, same as every other bespoke step does.
   */
  if (hasAnyInvestingStep(definition)) values.investors = [emptyInvestor()]
  /*
   * The recorded values, like `tags`: part of the state without being a field.
   * Empty is the answer for an optional step nobody visited.
   */
  if (recordsMarks(definition)) values[MARKS_FIELD] = []
  /*
   * The logged cash flows, like `prices`: part of the state without being a
   * field. Empty is the answer for an optional step nobody visited.
   */
  if (hasCashFlows(definition)) values[CASH_FLOWS_FIELD] = []
  /*
   * A number field starts undefined rather than `''`: its control holds a
   * number, and an empty string would be a value of the wrong type sitting in
   * the form until the user touched it — which zod reads as "not a number"
   * rather than as "not filled in yet".
   */
  for (const field of allFields(definition)) {
    values[field.name] = field.control === 'number' ? undefined : ''
  }
  return { ...values, ...definition.constants }
}

/**
 * Field names the assistant supplied. Read from the answer rather than from the
 * values, so a field stays marked as read even after the user edits it in its
 * own step — and an empty answer field never counts as read.
 *
 * A name the sentence supported without stating is still a value the assistant
 * supplied, as is an owner account it resolved: those do not travel as
 * `aiField`s, so they are named here from the same seed that wrote them. A
 * match the step could not have offered is not filled, and is not marked.
 */
export const aiFilledFields = (
  definition: WizardDefinition,
  answer?: AssetWizardAnswer,
  context: WizardSeedContext = {},
): Set<string> => {
  if (!answer) return new Set()

  const filled = new Set<string>()
  for (const field of allFields(definition)) {
    if (!field.aiField) continue
    const value = answer.fields[field.aiField as AssetWizardField]
    if (value === undefined || value === null || value === '') continue
    filled.add(field.name)
  }

  if (
    answer.assetNameSuggestion &&
    allFields(definition).some(field => field.name === ASSET_NAME_FIELD)
  ) {
    filled.add(ASSET_NAME_FIELD)
  }

  const seeded = answer.ownerAccount
    ? seedOwnerAccount(answer.ownerAccount, context)
    : undefined
  if (seeded) {
    for (const name of ['ownerAccountId', 'ownerAccountName', 'ownerAccountType']) {
      if (!seeded[name]) continue
      if (allFields(definition).some(field => field.name === name)) filled.add(name)
    }
  }

  return filled
}

/**
 * Fills every untouched defaulted field with the default it promised.
 *
 * A defaulted field stays empty for as long as the wizard is open: the
 * placeholder says what happens if it is left alone, and that reads as an offer
 * rather than as a value the user chose. On submit the offer has to be honoured
 * — the records are created with a currency either way, and leaving it to
 * whatever the API assumes is the wizard saying one thing and doing another.
 *
 * A copy, per the repo's copy-before-mutate rule: this runs on the live form
 * values, and writing into them would mutate state the form owns.
 *
 * Empty when the source cannot be resolved — an unloaded repository has no
 * currency to offer, and inventing one would be worse than passing nothing.
 */
export const resolveDefaults = (
  definition: WizardDefinition,
  values: WizardValues,
  resolve: (source: WizardDefaultSource) => string,
): WizardValues => {
  const next = { ...values }

  for (const field of allFields(definition)) {
    if (!field.defaulted || !isEmpty(next[field.name])) continue
    next[field.name] = resolve(field.defaulted)
  }

  return next
}

/**
 * The ownership step's three values, from the account the read resolved.
 *
 * Nothing when the read named none, when it named an id the step cannot offer,
 * or when it described a new account of a type the step does not allow — in
 * every one of those the step asks the question itself, which is what it is
 * for.
 */
const seedOwnerAccount = (
  account: AssetWizardAccount,
  context: WizardSeedContext,
): WizardValues | undefined => {
  if (account.id) {
    const match = context.ownerAccounts?.find(item => item.id === account.id)
    if (!match) return undefined

    /*
     * The name and type are copied off the repository's own account, not off
     * the answer — which by the backend's rule carries neither beside an id.
     * Same three values `handlePick` writes, so the summary reads the same
     * however the account got there.
     */
    return {
      [ACCOUNT_MODE_FIELD]: WizardAccountMode.EXISTING,
      ownerAccountId: match.id,
      ownerAccountName: match.name,
      ownerAccountType: match.type,
    }
  }

  if (!account.name || !account.type) return undefined

  /*
   * A name that already names one of the repository's own accounts outweighs
   * the model's own type guess: nothing named the sentence's owner, so it
   * fell back to a generic label like "Personal Account" — and when a
   * repository already has an account by that name, a second one is not a
   * different account, it is a duplicate. The client is what holds the
   * account list, so this — like the id above — is checked here rather than
   * trusted from the answer.
   */
  const collision = context.ownerAccounts?.find(
    item => item.name.trim().toLowerCase() === account.name!.trim().toLowerCase(),
  )
  if (collision) {
    return {
      [ACCOUNT_MODE_FIELD]: WizardAccountMode.EXISTING,
      ownerAccountId: collision.id,
      ownerAccountName: collision.name,
      ownerAccountType: collision.type,
    }
  }

  if (context.ownerAccountTypes && !context.ownerAccountTypes.includes(account.type)) {
    return undefined
  }

  return {
    [ACCOUNT_MODE_FIELD]: WizardAccountMode.NEW,
    ownerAccountId: '',
    ownerAccountName: account.name,
    ownerAccountType: account.type,
  }
}

/**
 * One suggested investor's account half, resolved the same way
 * `seedOwnerAccount` resolves the ownership step's single account — matched
 * by id against what the step can actually offer, or by name against a
 * collision, so a proposal never reads as a second account with a name the
 * repository already has.
 *
 * Unlike `seedOwnerAccount`, an investor that cannot be resolved still keeps
 * its row: the count of entries is itself something the sentence supported,
 * and dropping one down to nothing would lose that along with the account it
 * could not place. What is lost instead is only the part that could not be
 * checked — the row opens on the ownership step's own empty state so the
 * user finishes what the read could not.
 */
const seedInvestorAccount = (
  investor: AssetWizardInvestor,
  context: WizardSeedContext,
): Pick<WizardInvestor, 'accountMode' | 'accountId' | 'accountName' | 'accountType'> => {
  if (investor.id) {
    const match = context.ownerAccounts?.find(item => item.id === investor.id)
    if (match) {
      return {
        accountMode: WizardAccountMode.EXISTING,
        accountId: match.id,
        accountName: match.name,
        accountType: match.type,
      }
    }
  }

  if (investor.name) {
    const collision = context.ownerAccounts?.find(
      item => item.name.trim().toLowerCase() === investor.name!.trim().toLowerCase(),
    )
    if (collision) {
      return {
        accountMode: WizardAccountMode.EXISTING,
        accountId: collision.id,
        accountName: collision.name,
        accountType: collision.type,
      }
    }

    if (investor.type && (!context.ownerAccountTypes || context.ownerAccountTypes.includes(investor.type))) {
      return {
        accountMode: WizardAccountMode.NEW,
        accountId: '',
        accountName: investor.name,
        accountType: investor.type,
      }
    }

    // A name with no usable type: still worth keeping so the user only has
    // to choose a type, not recall who this was from the sentence again.
    return {
      accountMode: WizardAccountMode.NEW,
      accountId: '',
      accountName: investor.name,
      accountType: '',
    }
  }

  const { accountMode, accountId, accountName, accountType } = emptyInvestor()
  return { accountMode, accountId, accountName, accountType }
}

/**
 * One suggested investor's share of the commitment, in the form's own units.
 *
 * The backend gives `share` (absolute), `percent` (of the whole commitment)
 * or neither — see AssetWizardInvestor. private_fund_subscribe_and_commit
 * always gives one; private_security may give neither, when the sentence
 * names more than one entity with no split stated between them, and that
 * row stays unsized here exactly as it would if the read had used `entities`
 * instead — see `seedEntities`. A percent is only resolvable once the
 * commitment itself is known, which is why this runs after the fields loop
 * in `seedValues` rather than beside `seedOwnerAccount`, whose account has
 * nothing to wait on.
 */
const seedInvestorShare = (investor: AssetWizardInvestor, amount?: number): number | undefined => {
  if (investor.share !== undefined) return investor.share
  if (investor.percent !== undefined && amount) return (amount * investor.percent) / 100
  return undefined
}

/**
 * The investing-entities step's rows, from the read's own list — one row per
 * entity the sentence supported, in the order it gave them. Shared by
 * private_fund_subscribe_and_commit and private_security: both answer with
 * `investors`, and this seeds either the same way.
 */
const seedInvestors = (
  investors: AssetWizardInvestor[],
  context: WizardSeedContext,
  amount?: number,
): WizardInvestor[] =>
  investors.map(investor => ({
    ...seedInvestorAccount(investor, context),
    share: seedInvestorShare(investor, amount),
  }))

/**
 * private_fund's own rows, from the read's own `entities` list — one row per
 * entity the sentence supported, in the order it gave them. There is no
 * share to compute: every entity here independently holds the full amount,
 * so `seedInvestorAccount` resolves each row the same way it resolves an
 * investor's own account half, `AssetWizardEntity` being the same shape
 * minus the share. private_security used to share this too, before it moved
 * to `investors` so a row could carry a split — see `seedInvestors`.
 */
const seedEntities = (entities: AssetWizardEntity[], context: WizardSeedContext): WizardInvestor[] =>
  entities.map(entity => ({
    ...seedInvestorAccount(entity, context),
    share: undefined,
  }))

/**
 * The cash-flows step's own rows, from the read's own list — one row per
 * capital call, fee or distribution the sentence supported, in the order it
 * gave them.
 *
 * `investorIndex` is carried over as-is: `AssetWizardCashFlow` addresses the
 * answer's own `investors` by position, and `seedInvestors` keeps that same
 * order, so the position that named one there still names the same row here.
 *
 * A flow is dropped rather than guessed at wherever a required part is
 * missing — an unresolvable investor position, no date, no amount, or a type
 * outside the vocabulary the step's own dropdown offers. Half a row is not a
 * suggestion the user can act on, only one they would have to fix first.
 */
const seedCashFlows = (
  cashFlows: AssetWizardCashFlow[],
  investorCount: number,
): WizardCashFlow[] =>
  cashFlows
    .filter((flow): flow is Required<AssetWizardCashFlow> =>
      flow.investorIndex !== undefined &&
      flow.investorIndex >= 0 &&
      flow.investorIndex < investorCount &&
      flow.date !== undefined &&
      flow.amount !== undefined &&
      flow.type !== undefined &&
      (isContribution(flow.type) || isDistribution(flow.type)))
    .map(flow => ({
      investorIndex: flow.investorIndex,
      date: flow.date,
      type: flow.type,
      amount: flow.amount,
    }))

/**
 * The Valuation step's own rows, from the read's own list — one row per
 * dated value the sentence supported, distinct from the purchase price or
 * commitment itself.
 *
 * Always seeded as `AssetPriceSource.MANUAL`: the read is a value the
 * sentence stated, the same kind of entry the step's own "Record a value"
 * card writes, never an override of the carrying value the way the step's
 * own Override option is.
 *
 * A mark is dropped rather than guessed at wherever a required part is
 * missing — no date, no value. Half a mark is not a suggestion the user can
 * act on, only one they would have to fix first.
 */
const seedMarks = (valuation: AssetWizardValuation[]): WizardMark[] =>
  valuation
    .filter((mark): mark is Required<AssetWizardValuation> =>
      mark.date !== undefined && mark.close !== undefined)
    .map(mark => ({
      date: mark.date,
      close: mark.close,
      source: AssetPriceSource.MANUAL,
    }))

/** Defaults with whatever the assistant could fill written over them. */
export const seedValues = (
  definition: WizardDefinition,
  answer?: AssetWizardAnswer,
  context: WizardSeedContext = {},
): WizardValues => {
  const values = wizardDefaults(definition)
  if (!answer) return values

  for (const field of allFields(definition)) {
    if (!field.aiField) continue
    const value = answer.fields[field.aiField as AssetWizardField]
    if (value === undefined || value === null) continue

    /*
     * The assistant answers in strings. An amount is a number in the form, so
     * it is read as one here — and a string the model sent that is not a
     * number is dropped rather than stored, leaving the field empty for the
     * user instead of failing validation with something they never typed.
     */
    if (field.control === 'number') {
      const parsed = Number(value)
      if (!Number.isNaN(parsed)) values[field.name] = parsed
      continue
    }

    values[field.name] = String(value)
  }
  /*
   * The flow's own tags, same as a cold open — see seedTags. The read never
   * carries tags of its own to seed with instead.
   */
  values.tags = seedTags(definition.defaultTags)

  /*
   * The account `accountName` names, when the repository already has it. Not a
   * field on any wizard — nothing renders it — but part of the form state, so
   * the create can link to that account rather than making a second one with
   * the same name. Dropped when it is not an account we know about.
   */
  if (
    holdsAccount(definition) &&
    answer.accountId &&
    context.accountIds?.includes(answer.accountId)
  ) {
    values.accountId = answer.accountId
  }

  /*
   * The mode this flow's own account-chooser step opens on, when it named an
   * account at all: an id that matched puts it on the picker with that
   * account already selected, exactly where picking one by hand would land.
   * A name with no id puts it on the create-new half instead, with that name
   * already sitting in the field the loop above just wrote — the default
   * `wizardDefaults` opened on would otherwise show a picker with nothing
   * selected in it, and the name the assistant read nowhere the user can see.
   */
  if (holdsAccount(definition) && values.accountName) {
    values[MORTGAGE_ACCOUNT_MODE_FIELD] = values.accountId
      ? WizardAccountMode.EXISTING
      : WizardAccountMode.NEW
  }

  if (answer.ownerAccount) {
    Object.assign(values, seedOwnerAccount(answer.ownerAccount, context) ?? {})
  }

  /*
   * The investing-entities step's own rows, in place of the single empty one
   * `wizardDefaults` opened with — after the fields loop, because a percent
   * needs the commitment `values.amount` just filled to resolve against.
   */
  if (answer.investors?.length && hasInvestors(definition)) {
    values.investors = seedInvestors(answer.investors, context, values.amount as number | undefined)
  }

  /*
   * private_fund's own entities step, same slot as the investors block above
   * but never both — `hasInvestors`/`hasEntities` are mutually exclusive per
   * flow. No amount to wait on: unlike a percent, there is no share here to
   * resolve against it.
   */
  if (answer.entities?.length && hasEntities(definition)) {
    values.investors = seedEntities(answer.entities, context)
  }

  /*
   * The cash-flows step's own rows, in place of the empty list `wizardDefaults`
   * opened with — after the investors/entities are seeded, since a position
   * only resolves once `values.investors` itself has that many rows.
   */
  if (answer.cashFlows?.length && hasCashFlows(definition)) {
    values[CASH_FLOWS_FIELD] = seedCashFlows(
      answer.cashFlows,
      (values.investors as WizardInvestor[] | undefined)?.length ?? 0,
    )
  }

  /*
   * The Valuation step's own rows, in place of the empty list `wizardDefaults`
   * opened with — every flow but loan carries this step, so the gate is the
   * step's own presence rather than any flow-specific field.
   */
  if (answer.valuation?.length && recordsMarks(definition)) {
    values[MARKS_FIELD] = seedMarks(answer.valuation)
  }

  /*
   * A name the sentence supported without stating. Written in rather than
   * offered: the field is one the user would otherwise have to fill by hand,
   * and a name derived from their own words is a better starting point than an
   * empty box. It is marked READ with everything else the assistant supplied.
   *
   * After the fields loop, and only over an empty one: the backend refuses to
   * send both, but a value the sentence actually stated is the stronger claim
   * and must win if one ever arrives.
   */
  if (answer.assetNameSuggestion && isEmpty(values[ASSET_NAME_FIELD])) {
    values[ASSET_NAME_FIELD] = answer.assetNameSuggestion.value
  }

  // Constants last: a value the flow fixes is not the assistant's to read.
  return { ...values, ...definition.constants }
}

/*
 * Provenance describes the field, not its current contents: a required field
 * stays marked required once filled, and the outstanding count in the footer is
 * what tracks how many are still owed.
 */
export const originFor = (
  field: WizardFieldDef,
  isAiFilled: boolean,
  values: WizardValues,
  isFixed = false,
): WizardFieldOrigin => {
  // Ahead of `isAiFilled`: a fixed value is the flow's, whatever the sentence
  // happened to say, and seedValues applies the constants last for the same
  // reason.
  if (isFixed) return WizardFieldOrigin.FIXED
  if (isAiFilled) return WizardFieldOrigin.READ

  /*
   * Ahead of the required check, and only once a default has actually landed: a
   * field can be both required and defaulted — the date is — and over a value
   * the wizard supplied itself, "Allposit will not guess this" is the one thing
   * the label must not say. Still empty means the default could not be
   * resolved, and then it really is owed by the user.
   */
  if (field.defaulted && !isEmpty(values[field.name])) return WizardFieldOrigin.DEFAULT

  if (isFieldRequired(field, values)) return WizardFieldOrigin.REQUIRED
  if (field.defaulted) return WizardFieldOrigin.DEFAULT
  return WizardFieldOrigin.OPTIONAL
}

export const originsFor = (
  definition: WizardDefinition,
  filled: Set<string>,
  values: WizardValues,
): Record<string, WizardFieldOrigin> => {
  const fixed = new Set(Object.keys(definition.constants ?? {}))

  return Object.fromEntries(
    allFields(definition).map(field => [
      field.name,
      originFor(field, filled.has(field.name), values, fixed.has(field.name)),
    ]),
  )
}

/*
 * Provenance as @ds draws it. The two vocabularies line up one to one, and this
 * is where that stays true: WizardFieldOrigin is the wizard's word for it,
 * FieldLabelMark is the design system's, and neither has to know the other's.
 */
const MARK_BY_ORIGIN: Record<WizardFieldOrigin, FieldLabelMark | undefined> = {
  [WizardFieldOrigin.READ]: 'ai',
  [WizardFieldOrigin.REQUIRED]: 'required',
  [WizardFieldOrigin.DEFAULT]: 'default',
  [WizardFieldOrigin.OPTIONAL]: 'optional',
  // No glyph. DSFieldLabel's four marks are a closed vocabulary the legend
  // explains, and "the wizard set this" is not one of the four.
  [WizardFieldOrigin.FIXED]: undefined,
}

export const markFor = (origin: WizardFieldOrigin): FieldLabelMark | undefined =>
  MARK_BY_ORIGIN[origin]

const isEmpty = (value: unknown) => value === undefined || value === null || value === ''

/** Required fields of one step the user has not supplied yet. */
export const outstandingFields = (
  step: WizardStepDef,
  values: WizardValues,
): WizardFieldDef[] =>
  (step.fields ?? []).filter(
    field => isFieldRequired(field, values) && isEmpty(values[field.name]),
  )

/**
 * Whether one investor row still owes something: an account, same as
 * `isInvestorResolved` already checks, or a share to size it by — a row
 * naming an entity with nothing committed is as unfinished as one naming
 * nothing at all.
 */
const isInvestorOutstanding = (investor: WizardInvestor) =>
  !isInvestorResolved(investor) || !investor.share || investor.share <= 0

/**
 * How much the investing-entities step still owes, the same count the rail
 * and the summary's own required mark both read off of — one number, so the
 * two can never disagree about whether this step is done.
 *
 * A row missing its account or its share is counted per row, the same way an
 * ordinary step counts one outstanding field per empty control. Once every
 * row is individually resolved the one thing left to owe is the split itself
 * — the schema's own rule, restated here rather than run through zod just to
 * read a count off it — so a total that does not add up still counts as one.
 */
export const investorsOutstandingCount = (investors: WizardInvestor[], amount?: number): number => {
  // No row at all is the one state `wizardDefaults` never leaves this step
  // in — it always seeds one empty row — but the rule it would be breaking
  // is the same one a totally empty row already breaks: at least one investor.
  if (!investors.length) return 1

  const rows = investors.filter(isInvestorOutstanding).length
  if (rows) return rows

  if (amount === undefined) return 0

  const total = investors.reduce((sum, investor) => sum + (investor.share ?? 0), 0)
  return Math.abs(total - amount) > 0.01 ? 1 : 0
}

/**
 * How much the entities step still owes — private_fund's own sibling of
 * `investorsOutstandingCount`, minus the share entirely: each row becomes its
 * own full `add position` record, so there is no split to size and nothing
 * to sum against the commitment. A row is owed only while its account is.
 */
export const entitiesOutstandingCount = (investors: WizardInvestor[]): number => {
  // Same reasoning as `investorsOutstandingCount`: `wizardDefaults` always
  // seeds one empty row, so no rows at all breaks the same rule an empty one
  // already would — at least one entity.
  if (!investors.length) return 1

  return investors.filter(investor => !isInvestorResolved(investor)).length
}

export const missingFields = (
  definition: WizardDefinition,
  values: WizardValues,
): WizardMissingField[] => {
  const fields = definition.steps.flatMap(step =>
    outstandingFields(step, values).map(field => ({ key: field.name, label: field.label })),
  )

  /*
   * The one step the generic pass above cannot see: it tracks named fields,
   * and an investor row is never one — see WizardInvestor. One chip stands
   * for the whole step regardless of how many rows owe something, the same
   * way the rail states one count rather than one line per row.
   */
  const investorsStep = investorsStepOf(definition)
  if (investorsStep && investorsOutstandingCount(values.investors ?? [], values.amount as number | undefined)) {
    fields.push({ key: INVESTORS_STEP_KEY, label: investorsStep.label })
  }

  /** Same reasoning, for private_fund's own entities step — see `entitiesOutstandingCount`. */
  const entitiesStep = entitiesStepOf(definition)
  if (entitiesStep && entitiesOutstandingCount(values.investors ?? [])) {
    fields.push({ key: ENTITIES_STEP_KEY, label: entitiesStep.label })
  }

  return fields
}

/**
 * The rail. Every step is listed and every step is reachable — a step with
 * nothing outstanding reads as complete, one still being built as skipped, and
 * neither of those is a reason to hide it.
 */
export const wizardSteps = (
  definition: WizardDefinition,
  values: WizardValues,
  visited: string[] = [],
): WizardStep[] =>
  definition.steps.map(step => {
    /*
     * An optional step reads as done when it holds something, and otherwise as
     * whichever of the two empties it is: skipped once the user has opened it
     * and left it, optional until then. "Skipped" is a claim about what they
     * did, and it is not true of a step nobody has been to.
     *
     * Never incomplete either way — there is nothing here to owe.
     */
    const emptyState = (key: string) =>
      visited.includes(key) ? WizardStepState.SKIPPED : WizardStepState.OPTIONAL

    if (step.key === TAGS_STEP_KEY) {
      return {
        key: step.key,
        label: step.label,
        state: values.tags?.length ? WizardStepState.COMPLETE : emptyState(step.key),
      }
    }

    if (step.key === VALUE_STEP_KEY && step.component) {
      return {
        key: step.key,
        label: step.label,
        state: values[MARKS_FIELD]?.length ? WizardStepState.COMPLETE : emptyState(step.key),
      }
    }

    if (step.key === CASH_FLOWS_STEP_KEY && step.component) {
      return {
        key: step.key,
        label: step.label,
        state: values[CASH_FLOWS_FIELD]?.length ? WizardStepState.COMPLETE : emptyState(step.key),
      }
    }

    /*
     * Never optional and never skippable — a fund with no investing entity has
     * nothing to hang its commitment off. Unlike the tags/value steps above,
     * an empty read here is not a fair starting point, so there is no
     * `emptyState` branch: it is either done or it is owed.
     */
    if (step.key === INVESTORS_STEP_KEY) {
      const count = investorsOutstandingCount(values.investors ?? [], values.amount as number | undefined)
      return {
        key: step.key,
        label: step.label,
        state: count ? WizardStepState.INCOMPLETE : WizardStepState.COMPLETE,
        count: count || undefined,
      }
    }

    /** Same reasoning as INVESTORS_STEP_KEY above, minus the share — see `entitiesOutstandingCount`. */
    if (step.key === ENTITIES_STEP_KEY) {
      const count = entitiesOutstandingCount(values.investors ?? [])
      return {
        key: step.key,
        label: step.label,
        state: count ? WizardStepState.INCOMPLETE : WizardStepState.COMPLETE,
        count: count || undefined,
      }
    }

    if (step.key === SUMMARY_STEP_KEY) {
      const outstanding = missingFields(definition, values).length
      return {
        key: step.key,
        label: step.label,
        state: outstanding ? WizardStepState.INCOMPLETE : WizardStepState.COMPLETE,
      }
    }

    // Not built yet: nothing here can be outstanding, so it cannot block create.
    if (!step.fields?.length) {
      return { key: step.key, label: step.label, state: WizardStepState.SKIPPED }
    }

    const count = outstandingFields(step, values).length
    return {
      key: step.key,
      label: step.label,
      state: count ? WizardStepState.INCOMPLETE : WizardStepState.COMPLETE,
      count: count || undefined,
    }
  })

/** A record's own field that carries the money — a name, not a fixed one:
 * the mortgage half of real-estate-with-financing calls it "principal"
 * because it collects a lender, not a purchase, but it is still the number
 * the done screen shows and nets against the position it is secured by. */
const RECORD_AMOUNT_FIELDS = ['amount', 'principal']

/**
 * One record the create actually wrote, shaped for the done screen: what
 * icon and label it carries, what it says about itself, and what it is
 * worth — signed, so a liability nets against a position rather than only
 * looking like a second thing that was added.
 */
export type CreatedRecordRow = {
  asset: AssetClass
  icon: Component
  label: string
  /** e.g. "Position added Mar 14, 2026" or "Secured by Cedar Hill Residence". */
  detail: string
  /** Signed — negative for a liability. Absent when the record's own amount
   * field could not be read off the submitted values. */
  amount?: number
}

/**
 * The done screen's rows, one per record the create wrote — paired by index
 * with `definition.records`, which is the same order `create()` returns them
 * in (see every flow's own create.ts: the property before the mortgage it
 * secures, never the reverse).
 *
 * The amount is read back off the submitted values rather than off the asset:
 * an `AssetClass` carries no notion of what it was bought for, only what it is
 * now worth, and the record that wrote it already had the number in hand.
 *
 * Clamped against the record list, not indexed into it directly: a fund split
 * across investors writes one bundle per investor from a single `records`
 * entry, so there can be more assets than static record definitions. Every
 * extra one repeats the last definition rather than reading past the end of
 * the array into `undefined`.
 */
export const createdRecordRows = (
  definition: WizardDefinition,
  assets: AssetClass[],
  values: WizardValues,
): CreatedRecordRow[] => {
  const dateLabel = stringToLocalDateString(values.date) ?? values.date ?? ''
  const investors: WizardInvestor[] | undefined = values.investors

  return assets.map((asset, index) => {
    const record = definition.records[Math.min(index, definition.records.length - 1)]
    const isLiability = record?.transactionType === 'add liability'

    /*
     * An investor's own share when this flow split its commitment across more
     * than one — see WizardInvestor — otherwise the record's own named field,
     * a shared value every bundle this flow writes carries the same way.
     */
    const investorShare = investors?.[index]?.share
    const amountField = record?.fields.find(name => RECORD_AMOUNT_FIELDS.includes(name))
    const rawAmount = investorShare !== undefined
      ? investorShare
      : amountField ? Number(values[amountField]) : NaN
    const amount = Number.isNaN(rawAmount) ? undefined : (isLiability ? -rawAmount : rawAmount)

    /*
     * "Secured by" only where there is a second record to point at — a loan
     * with nothing bought against it is still a liability, but there is
     * nothing here for it to be secured by.
     */
    const detail = investors?.[index]
      ? `Held in ${investors[index].accountName}`
      : isLiability && assets.length > 1
        ? `Secured by ${assets[0].data.name}`
        : `${isLiability ? 'Liability' : 'Position'} added ${dateLabel}`

    return {
      asset,
      icon: record?.icon ?? definition.icon,
      label: record?.label ?? definition.label,
      detail,
      amount,
    }
  })
}

/**
 * What these records did to the balance sheet, together — a position's
 * amount less a liability's, the way a financed property adds less equity
 * than it cost. Absent for a single record: there is nothing to net it
 * against, and the row would only repeat the amount already on screen.
 *
 * Also absent whenever a row's own amount could not be read — a partial sum
 * would understate what changed rather than honestly saying it does not know.
 */
export const netEquityOf = (rows: CreatedRecordRow[]): number | undefined => {
  if (rows.length < 2) return undefined
  if (rows.some(row => row.amount === undefined)) return undefined

  return rows.reduce((sum, row) => sum + (row.amount ?? 0), 0)
}

/**
 * The done screen's opening line: what it made, and where.
 *
 * Named only for one record — the account a financed property's mortgage
 * hangs off is its lender, not the property's own owner account, and
 * claiming both records landed in the same place would be wrong for exactly
 * the flow this screen most often shows two rows for.
 */
export const createdSummaryText = (rows: CreatedRecordRow[], values: WizardValues): string => {
  if (rows.length !== 1) return `${rows.length} records were added to your balance sheet.`

  const accountName = values.ownerAccountName
  return accountName
    ? `${rows[0].asset.data.name} was added to ${accountName}.`
    : `${rows[0].asset.data.name} was added to your balance sheet.`
}

/**
 * The account these records now hang off, when the wizard made it rather
 * than pointing at one the repository already had — the done screen's cue to
 * say so and offer a way to it, since a brand new account is otherwise
 * nowhere else on screen but the ownership step already left behind.
 */
export const newlyCreatedAccountName = (values: WizardValues): string | undefined => {
  if (values[ACCOUNT_MODE_FIELD] !== WizardAccountMode.NEW) return undefined
  return values.ownerAccountName || undefined
}
